import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBullhorn,
    faTag,
    faGift,
    faEnvelope,
    faChartBar,
    faArrowRight,
    faCheckCircle,
    faUserClock,
    faImage
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function MarketingIndex() {
    const sections = [
        {
            id: 'ofertas',
            title: 'Gestión de Ofertas',
            icon: faTag,
            description: 'Crea y gestiona ofertas promocionales para aumentar ventas y fidelizar clientes',
            path: '/documentation/marketing/ofertas',
            status: 'disponible',
            features: [
                'Crear diferentes tipos de ofertas (descuentos, 2x1, envío gratis)',
                'Programar ofertas con fechas de inicio y fin',
                'Establecer límites de uso y condiciones',
                'Analizar el rendimiento de tus ofertas',
                'Gestionar estados (borrador, activa, pausada, finalizada)'
            ]
        },
        {
            id: 'winback',
            title: 'Recuperar Clientes Perdidos',
            icon: faUserClock,
            description: 'Sistema inteligente para recuperar clientes inactivos automáticamente',
            path: '/documentation/marketing/winback',
            status: 'disponible',
            features: [
                'Sistema inteligente que calcula el momento óptimo para contactar cada cliente',
                'Segmentación RFM automática para identificar clientes en riesgo',
                'Envío automático o manual de campañas de recuperación',
                'Métricas y análisis del rendimiento de las campañas',
                'Configuración personalizable de umbrales y multiplicadores'
            ]
        },
        {
            id: 'recompensas',
            title: 'Gestión de Recompensas',
            icon: faGift,
            description: 'Crea y gestiona premios y descuentos para tus clientes',
            path: '/documentation/marketing/recompensas',
            status: 'disponible',
            features: [
                'Crear premios canjeables con puntos',
                'Crear descuentos monetarios o porcentuales',
                'Enviar premios directamente a clientes',
                'Gestionar el canje de premios',
                'Aplicar descuentos a tickets de compra'
            ]
        },
        {
            id: 'email-marketing',
            title: 'Email Marketing',
            icon: faEnvelope,
            description: 'Crea y envía campañas de email marketing personalizadas',
            path: '/documentation/marketing/email-marketing',
            status: 'disponible',
            features: [
                'Editor WYSIWYG para crear emails visualmente',
                'Segmentación avanzada con análisis RFM',
                'Envío masivo con control automático de velocidad',
                'Estadísticas detalladas de cada campaña',
                'Gestión de contactos y cumplimiento GDPR'
            ]
        },
        {
            id: 'infografias',
            title: 'Generar Infografías',
            icon: faImage,
            description: 'Crea infografías profesionales con editor visual',
            path: '/documentation/marketing/infografias',
            status: 'disponible',
            features: [
                'Templates predefinidos para empezar rápido',
                'Editor visual con múltiples tipos de elementos',
                'Personalización completa de colores, fuentes y estilos',
                'Exportación en PDF y PNG de alta calidad',
                'Autoguardado automático cada 30 segundos'
            ]
        },
        {
            id: 'campanas',
            title: 'Campañas',
            icon: faBullhorn,
            description: 'Diseña y ejecuta campañas de marketing dirigidas a segmentos específicos',
            path: '/documentation/marketing/campanas',
            status: 'proximamente',
            features: [
                'Crear campañas personalizadas',
                'Segmentar audiencia por comportamiento',
                'Programar envíos automáticos',
                'Medir resultados y ROI'
            ]
        },
        {
            id: 'recompensas',
            title: 'Recompensas',
            icon: faGift,
            description: 'Configura programas de recompensas y fidelización para tus clientes',
            path: '/documentation/marketing/recompensas',
            status: 'proximamente',
            features: [
                'Diseñar programas de puntos',
                'Crear premios y recompensas',
                'Configurar reglas de acumulación',
                'Gestionar redención de premios'
            ]
        },
        {
            id: 'comunicacion',
            title: 'Comunicación',
            icon: faEnvelope,
            description: 'Envía mensajes personalizados a tus clientes por diferentes canales',
            path: '/documentation/marketing/comunicacion',
            status: 'proximamente',
            features: [
                'Envío de emails promocionales',
                'Mensajes SMS y WhatsApp',
                'Notificaciones push',
                'Plantillas personalizables'
            ]
        },
        {
            id: 'estadisticas',
            title: 'Estadísticas de Marketing',
            icon: faChartBar,
            description: 'Analiza el rendimiento de tus acciones de marketing y campañas',
            path: '/documentation/marketing/estadisticas',
            status: 'proximamente',
            features: [
                'Métricas de ofertas y campañas',
                'Análisis de conversión',
                'ROI de acciones de marketing',
                'Reportes personalizados'
            ]
        }
    ];

    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
                        <FontAwesomeIcon icon={faBullhorn} className="text-purple-600 text-4xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Marketing y Promociones
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Aprende a utilizar las herramientas de marketing de PetitPrime para aumentar tus ventas, 
                        fidelizar clientes y hacer crecer tu negocio de manera efectiva.
                    </p>
                </div>

                {/* Introducción */}
                <div className="bg-white rounded-lg shadow-md p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                        ¿Qué encontrarás en esta sección?
                    </h2>
                    <p className="text-gray-600 mb-4">
                        El módulo de Marketing de PetitPrime te permite crear y gestionar estrategias promocionales 
                        completas para tu negocio. Desde ofertas especiales hasta campañas dirigidas, aquí aprenderás 
                        a utilizar todas las herramientas disponibles para maximizar el impacto de tus acciones de marketing.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <div className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 mr-3" />
                            <div>
                                <h3 className="font-semibold text-gray-800">Ofertas Promocionales</h3>
                                <p className="text-sm text-gray-600">
                                    Crea descuentos, ofertas especiales y promociones que atraigan a tus clientes
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 mr-3" />
                            <div>
                                <h3 className="font-semibold text-gray-800">Campañas Dirigidas</h3>
                                <p className="text-sm text-gray-600">
                                    Diseña campañas personalizadas para segmentos específicos de clientes
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 mr-3" />
                            <div>
                                <h3 className="font-semibold text-gray-800">Programas de Fidelización</h3>
                                <p className="text-sm text-gray-600">
                                    Implementa sistemas de puntos y recompensas para fidelizar clientes
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 mr-3" />
                            <div>
                                <h3 className="font-semibold text-gray-800">Comunicación Multi-canal</h3>
                                <p className="text-sm text-gray-600">
                                    Envía mensajes por email, SMS, WhatsApp y otros canales
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Secciones Disponibles */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">
                        Guías Disponibles
                    </h2>
                    <div className="space-y-4">
                        {sections.map((section) => (
                            <div
                                key={section.id}
                                className={`bg-white rounded-lg shadow-md p-6 transition-all hover:shadow-lg ${
                                    section.status === 'disponible' ? 'border-l-4 border-purple-500' : 'opacity-75'
                                }`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start flex-1">
                                        <div className={`p-3 rounded-lg mr-4 ${
                                            section.status === 'disponible' ? 'bg-purple-100' : 'bg-gray-100'
                                        }`}>
                                            <FontAwesomeIcon
                                                icon={section.icon}
                                                className={`text-xl ${
                                                    section.status === 'disponible' ? 'text-purple-600' : 'text-gray-400'
                                                }`}
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-xl font-bold text-gray-800">
                                                    {section.title}
                                                </h3>
                                                {section.status === 'proximamente' && (
                                                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">
                                                        Próximamente
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-600 mb-4">
                                                {section.description}
                                            </p>
                                            {section.status === 'disponible' && section.features && (
                                                <ul className="space-y-2 mb-4">
                                                    {section.features.map((feature, index) => (
                                                        <li key={index} className="flex items-start text-sm text-gray-600">
                                                            <FontAwesomeIcon
                                                                icon={faCheckCircle}
                                                                className="text-green-500 mr-2 mt-0.5 text-xs"
                                                            />
                                                            {feature}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            {section.status === 'disponible' && (
                                                <Link
                                                    to={section.path}
                                                    className="inline-flex items-center text-purple-600 hover:text-purple-700 font-semibold"
                                                >
                                                    Ver guía completa
                                                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Consejos */}
                <div className="bg-purple-50 rounded-lg p-6 mb-8">
                    <h3 className="text-lg font-semibold text-purple-800 mb-3">
                        💡 Consejos para un Marketing Efectivo
                    </h3>
                    <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start">
                            <span className="text-purple-600 mr-2">•</span>
                            <span>Define objetivos claros antes de crear ofertas o campañas</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-purple-600 mr-2">•</span>
                            <span>Analiza el rendimiento de tus acciones para optimizar futuras campañas</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-purple-600 mr-2">•</span>
                            <span>Segmenta tu audiencia para enviar mensajes más relevantes</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-purple-600 mr-2">•</span>
                            <span>Programa ofertas con suficiente antelación para maximizar su impacto</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-purple-600 mr-2">•</span>
                            <span>Combina diferentes tipos de ofertas para mantener el interés de tus clientes</span>
                        </li>
                    </ul>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-6 border-t">
                    <Link
                        to="/documentation/gestion"
                        className="text-gray-600 hover:text-gray-800 font-medium"
                    >
                        ← Volver a Gestión
                    </Link>
                    <Link
                        to="/documentation/marketing/ofertas"
                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center"
                    >
                        Comenzar con Ofertas
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default MarketingIndex;

